import { Datepicker } from "@mobiscroll/react";
import moment from "moment-timezone";
import React, { useState } from "react";
import "../i18n/moment";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grow,
  Skeleton,
  Stack,
  useColorScheme
} from "@mui/material";
import { MeetingAutobookInfo } from "../components/Meeting/MeetingAutobookInfo";
import { useSchedulerStore } from "../state/scheduler.store";

import "../../src/assets/mobiscroll.css";
import { range } from "lodash-es";
import { useTranslation } from "react-i18next";
import { PoweredByText } from "../components/Branding/PoweredBy";
import { BookingForm } from "../components/Forms/BookingForm";
import { AltChatUI } from "../components/Oliver/AltChatUI";
import { useI18nLanguage } from "../hooks/i18nLanguage";
import { useAutobookDatePicker } from "../hooks/useDatePicker";
import { useLegalTerms } from "../hooks/useLegalTerms";
import { useScreenSize } from "../hooks/useScreenSize";
import { getMobiscrollLocale } from "../i18n/mobiscroll";
import { Participant } from "../services/api/types/availability.type";
import { BookingFormType, LegalType } from "../types";
import { getSlotCountMobiscrollDatePickerLabels } from "../utils/slots";

interface IMonthViewProps {
  currentlySelectedSlot: string | null;
  previouslySelectedTime: string | null;
  handleTimezoneChange: (tz: string) => void;
  handleFormInput: (form: BookingFormType, legal: LegalType[]) => void;
}

export const MonthView: React.FC<IMonthViewProps> = ({
  currentlySelectedSlot,
  previouslySelectedTime,
  handleTimezoneChange,
  handleFormInput
}) => {
  const { t, i18n } = useTranslation();
  const { isLargeScreen } = useScreenSize();
  const { mode: themeVariant } = useColorScheme();
  const { minDate, maxDate, selectedDate } = useAutobookDatePicker();
  const { selectedLanguageForMoment } = useI18nLanguage();

  const {
    meetingLink,
    host,
    sessionData,
    actions,
    clientId,
    ui,
    clientLogo,
    loadingAvailability
  } = useSchedulerStore();
  const legalTerms = useLegalTerms();

  const [inlineOpen, setInlineOpen] = useState(false);

  const handleSlotSelect = (slot: {
    hour: number;
    minute: number;
    participants: Participant[];
  }) => {
    actions.setSessionData({
      ...sessionData,
      selectedSlot: {
        hour: slot.hour,
        minute: slot.minute,
        date: moment(selectedDate).startOf("day").valueOf(),
        participants: slot.participants || []
      }
    });

    if (ui?.currentView === "month") {
      setInlineOpen(true);
      actions.setUiState({ ...ui, currentStep: "form_inline" });
    }
  };

  const handleDateChange = (c: { value: { valueOf: () => number } }) => {
    if (c.value?.valueOf() && ui?.currentStep === "initial") {
      actions.setSessionData({
        ...sessionData,
        prevSelectedSlot: sessionData?.prevSelectedSlot,
        selectedSlot: {
          date: moment(c?.value.valueOf()).valueOf(),
          hour: null,
          minute: null
        }
      });
    }
  };

  const handleFormBack = () => {
    setInlineOpen(false);
    actions.setUiState({
      ...ui,
      currentStep: "initial",
      currentView: "month"
    });
  };

  const isOliverChatEnabled =
    localStorage.getItem("oliverChatEnabled") === "true";

  return (
    <Grow in={true} style={{ transformOrigin: "1 2 2" }} timeout={150}>
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        sx={{
          width: 1,
          height: !isLargeScreen ? "auto" : "100dvh"
        }}
      >
        <Card
          variant="outlined"
          raised={false}
          sx={{
            width: "100%",
            minWidth: "380px",
            maxWidth: isLargeScreen ? 1074 : null,
            minHeight: !isLargeScreen ? "100dvh" : null
          }}
        >
          <Stack
            direction={!isLargeScreen ? "column" : "row"}
            divider={
              <Divider
                orientation={!isLargeScreen ? "horizontal" : "vertical"}
                flexItem
              />
            }
          >
            {/* Meeting Info Section */}
            <Box
              sx={{
                p: 2.5,
                pt: 1,
                flex: 1,
                maxWidth: !isLargeScreen ? "100%" : "360px"
              }}
            >
              <MeetingAutobookInfo
                isRoosterLink={meetingLink?.type === "rooster_links"}
                clientLogo={meetingLink ? clientLogo : undefined}
                uiStep={ui?.currentStep ?? "loading"}
                meetingDuration={meetingLink?.availability?.duration ?? null}
                host={host ?? null}
                selectedTimezone={sessionData?.selectedTimezone ?? null}
                participants={meetingLink?.interviewers ?? []}
                timeSlots={{
                  previouslySelectedTime,
                  currentlySelectedSlot
                }}
                handleTimezoneChange={handleTimezoneChange}
              />
            </Box>

            {/* Calendar and Time Slots Section */}
            <Box
              sx={{
                flex: !isLargeScreen ? 1 : 2,
                minWidth: !isLargeScreen
                  ? "100%"
                  : inlineOpen && !isOliverChatEnabled
                    ? "650px"
                    : "550px"
              }}
            >
              {inlineOpen ? (
                <Box sx={{ p: 3, height: "100%" }}>
                  <BookingForm
                    formType={
                      meetingLink?.roosterLinksMeta?.profileType === "prospect"
                        ? "Prospect"
                        : "Standalone"
                    }
                    legal={legalTerms}
                    isReschedule={sessionData?.isRescheduleFlow ?? false}
                    isAutobook={meetingLink?.type === "rooster"}
                    candidateRequiredFields={
                      meetingLink?.roosterLinksMeta?.candidateRequiredFields ??
                      []
                    }
                    savedFormData={sessionData?.formData ?? {}}
                    submitHandler={handleFormInput}
                    backHandler={handleFormBack}
                  />
                </Box>
              ) : (
                <Stack
                  direction={!isLargeScreen ? "column" : "row"}
                  divider={
                    <Divider
                      orientation={!isLargeScreen ? "horizontal" : "vertical"}
                      flexItem
                    />
                  }
                >
                  {/* Calendar */}
                  <Box
                    sx={{
                      py:
                        sessionData?.computedAvailability &&
                        sessionData.computedAvailability.length > 0
                          ? 1
                          : 0,
                      flex: 2,
                      minWidth: !isLargeScreen ? "100%" : "310px"
                    }}
                  >
                    {clientId && !loadingAvailability && i18n.language ? (
                      <Datepicker
                        selectMultiple={false}
                        display="inline"
                        calendarType="month"
                        showOuterDays={true}
                        showRangeLabels={true}
                        onChange={handleDateChange}
                        valid={sessionData?.computedAvailability?.map(
                          (a) => a.date
                        )}
                        value={selectedDate}
                        min={minDate}
                        max={maxDate}
                        labels={getSlotCountMobiscrollDatePickerLabels(
                          sessionData?.computedAvailability,
                          t
                        )}
                        themeVariant={
                          themeVariant === "light" ? "light" : "dark"
                        }
                        locale={getMobiscrollLocale(t, i18n.language)}
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        height="100%"
                        width="100%"
                      />
                    )}
                  </Box>

                  {/* Time Slots */}
                  <Box
                    sx={{
                      p: 2,
                      flex: 1,
                      maxWidth: !isLargeScreen ? "100%" : "280px",
                      overflowY: !isLargeScreen ? "hidden" : "auto"
                    }}
                  >
                    <Stack
                      key={loadingAvailability ? "loading" : "loaded"}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Box sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {clientId && !loadingAvailability ? (
                          `${moment(sessionData?.selectedSlot?.date)
                            .locale(selectedLanguageForMoment)
                            .format(
                              "ddd, MMMM"
                            )} ${moment(sessionData?.selectedSlot?.date).format("DD")}`
                        ) : (
                          <Skeleton variant="text" width={110} height="28px" />
                        )}
                      </Box>
                      <Chip
                        label={
                          clientId &&
                          !loadingAvailability &&
                          sessionData?.computedAvailability?.length ? (
                            <span>
                              {
                                sessionData?.computedAvailability?.find((d) =>
                                  d.date.includes(
                                    moment(
                                      sessionData?.selectedSlot?.date
                                    ).format("YYYY-MM-DD")
                                  )
                                )?.availability.length
                              }{" "}
                              {t("ROOSTER.PUBLIC-APP.SLOT", {
                                defaultValue: "slots"
                              })}
                            </span>
                          ) : (
                            <Skeleton variant="text" width={40} />
                          )
                        }
                        color="default"
                        size="small"
                      />
                    </Stack>

                    <Stack
                      spacing={1.5}
                      sx={{
                        maxHeight: isLargeScreen ? "480px" : null,
                        minHeight: isLargeScreen ? "480px" : null,
                        overflow: isLargeScreen ? "auto" : "hidden",
                        pr: "3px",
                        "&::-webkit-scrollbar": {
                          width: "4px",
                          borderRadius: "10px"
                        },
                        "&:hover": {
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#888",
                            borderRadius: "10px"
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "#555"
                          }
                        }
                      }}
                    >
                      {clientId && !loadingAvailability ? (
                        sessionData?.computedAvailability
                          ?.find((d) =>
                            d.date.includes(
                              moment(selectedDate).format("YYYY-MM-DD")
                            )
                          )
                          ?.availability?.map?.((slot) => {
                            return (
                              <Button
                                key={slot.hour + slot.minute}
                                sx={
                                  themeVariant === "light"
                                    ? {
                                        borderColor: "#375990",
                                        color: "#375990"
                                      }
                                    : {
                                        borderColor: "#565656",
                                        color: "#9c9c9c",
                                        backgroundColor: "#1f1f1f",
                                        ":hover": {
                                          backgroundColor: "#282828"
                                        }
                                      }
                                }
                                variant="outlined"
                                onClick={() => {
                                  handleSlotSelect(slot);
                                }}
                              >
                                {moment()
                                  .startOf("day")
                                  .set("hour", slot.hour)
                                  .set("minutes", slot.minute)
                                  .locale(selectedLanguageForMoment)
                                  .format("LT")}
                              </Button>
                            );
                          })
                      ) : (
                        <>
                          {range(0, 10).map((_) => (
                            <Skeleton
                              key={_}
                              variant="rounded"
                              width={"100%"}
                              height={"35px"}
                            />
                          ))}
                        </>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Box>
            <Stack
              direction={!isLargeScreen ? "column" : "row"}
              divider={
                <Divider
                  orientation={!isLargeScreen ? "horizontal" : "vertical"}
                  flexItem
                />
              }
            >
              <AltChatUI view="monthView" showChatIcon={!isLargeScreen} />
            </Stack>
          </Stack>
        </Card>
        <Box
          sx={{
            width: "100%",
            maxWidth: isLargeScreen ? 1074 : null,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mr: 1
          }}
        >
          <PoweredByText show={isLargeScreen} />
        </Box>
      </Stack>
    </Grow>
  );
};
